import {Box, BoxProps, Typography} from "@mui/material";
import React, {FC, useEffect, useRef} from "react";
import {makeStyles} from "tss-react/mui";
import {ICourse} from "@buildwitt/shapeshift-types/ICourse";
import {IAPIDrivenChildrenFunction} from "@buildwitt/shapeshift-common/types/ICommon";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useEnrolledCourses} from "@/app2/pages/HomePage/hooks/useEnrolledCourses";
import EventAvailable from "@mui/icons-material/EventAvailable";
import {palette} from "@/constants";
import {EnrolledCourseCard} from "./EnrolledCourseCard";
import {EnrolledCourseCardSkeleton} from "./EnrolledCourseCardSkeleton";
import SectionHeader from "../Components/SectionHeader";

interface IPropsCustom {
	data?: ICourse[];
	children?: IAPIDrivenChildrenFunction<ICourse[] | undefined>;
}

export type IProps = Omit<BoxProps, "children"> & IPropsCustom;

const useStyles = makeStyles()((theme) => ({
	container: {
		width: "100%",
		marginBottom: theme.customSpacing.carouselGap
	},
	scrollContainer: {
		display: "flex",
		flexWrap: "nowrap",
		overflowX: "auto",
		gap: "10px",
		paddingBottom: theme.spacing(2),
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": {
			display: "none"
		},
		cursor: "grab",
		"&:active": {
			cursor: "grabbing"
		},
		userSelect: "none"
	},
	cardsContainer: {
		position: "relative",
		width: "100%"
	},
	gradientOverlayRight: {
		position: "absolute",
		top: 0,
		right: 0,
		width: "40px",
		height: "100%",
		background: "linear-gradient(to right, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0.3) 50%, rgba(14, 14, 14, 0.7) 80%, #0E0E0E 100%)",
		pointerEvents: "none",
		zIndex: 1
	},
	gradientOverlayLeft: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "40px",
		height: "100%",
		background: "linear-gradient(to left, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0.3) 50%, rgba(14, 14, 14, 0.7) 80%, #0E0E0E 100%)",
		pointerEvents: "none",
		zIndex: 1
	},
	nothingScheduledContainer: {
		minHeight: "476px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		padding: "50px",
		textAlign: "center",
		boxSizing: "border-box",
		"svg": {
			margin: "10px auto",
			width: "100px",
			height: "100px",
			"path": {
				fill: palette.greySolid
			}
		}
	}
}));

export const EnrolledCoursesSection: FC<IProps> = () => {
	const {t} = useSSTranslation();
	const {classes} = useStyles();
	const {enrolledCourses, isLoading} = useEnrolledCourses();
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const SCROLL_AMOUNT = 330;
	const [canScrollLeft, setCanScrollLeft] = React.useState(false);
	const [canScrollRight, setCanScrollRight] = React.useState(false);

	const scrollLeft = (): void => {
		scrollRef.current?.scrollBy({
			left: -SCROLL_AMOUNT,
			behavior: "smooth"
		});
	};

	const scrollRight = (): void => {
		scrollRef.current?.scrollBy({
			left: SCROLL_AMOUNT,
			behavior: "smooth"
		});
	};

	const updateScrollButtons = (): void => {
		const el = scrollRef.current;
		if (!el) return;

		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
	};

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		updateScrollButtons();

		el.addEventListener("scroll", updateScrollButtons);
		window.addEventListener("resize", updateScrollButtons);

		return () => {
			el.removeEventListener("scroll", updateScrollButtons);
			window.removeEventListener("resize", updateScrollButtons);
		};
	}, [enrolledCourses]);

	return (
		<Box className={classes.container}>
			<SectionHeader
				title={t("home_enrolled_courses", "Enrolled Courses")}
				badgeContent={enrolledCourses?.length}
				onScrollLeft={scrollLeft}
				onScrollRight={scrollRight}
				canScrollLeft={canScrollLeft}
				canScrollRight={canScrollRight}
			/>
			<Box className={classes.cardsContainer}>
				{!isLoading && canScrollLeft && <Box className={classes.gradientOverlayLeft} />}
				{isLoading ? <EnrolledCourseCardSkeleton/> :
					<Box
						ref={scrollRef}
						className={classes.scrollContainer}
					>
						{enrolledCourses?.map((item) => (
							<EnrolledCourseCard
								key={item.id}
								course={item}
							/>
						))}
					</Box>}
				{!isLoading && canScrollRight && <Box className={classes.gradientOverlayRight} />}
			</Box>
			{!isLoading && enrolledCourses?.length === 0 &&
				<Box
					className={classes.nothingScheduledContainer}
					data-test-id="training-feed-nothing-scheduled"
					role="status"
					aria-label="No training modules scheduled for this day"
				>
					<EventAvailable aria-hidden="true" />
					<Typography
						variant="h5"
						data-test-id="training-feed-nothing-scheduled-text"
					>
						{t("training_no_courses_enrolled", "You’re not enrolled in any courses yet.")}
					</Typography>
				</Box>
			}
		</Box>
	);
};

export default EnrolledCoursesSection;
